'use client'

import {
    createContext,
    useContext,
    useMemo,
    useState,
    type KeyboardEvent,
    type MouseEvent,
    type ReactNode,
} from 'react'
import { cn } from '@/utils/styles/cn'

/**
 * Interactive descendants that own their own click — brand links, More details, CTAs, hover
 * overlays, form controls. A card open must never fire when the pointer landed here.
 */
export const RESOURCE_CARD_OPEN_EXEMPT =
    'button, a, input, textarea, select, [role="button"], [role="menuitem"], [data-card-open-exempt]'

/**
 * Whether a card open should be ignored for this event target, given the card root as boundary.
 *
 * The card root itself is `role="button"`. Without a boundary, `closest('[role="button"]')` always
 * matches the shell and every click is swallowed — no open, no `?creative_id=` / preview.
 */
export function shouldIgnoreResourceCardOpen(
    target: EventTarget | null,
    boundary?: Element | null
): boolean {
    if (!(target instanceof Element)) return true
    const match = target.closest(RESOURCE_CARD_OPEN_EXEMPT)
    // The root itself carries role="button"; only descendant interactives should suppress open.
    return match != null && match !== boundary
}

/**
 * How media videos play inside a ResourceCard.
 * - `hover` — play only while the pointer is over the media slot (Insights).
 * - `auto` — start muted playback as soon as the media mounts (Research).
 *
 * Flip surface defaults in {@link RESOURCE_CARD_VIDEO_PLAY_MODE}; domain media reads the live
 * decision via {@link useResourceCardVideo}.
 */
export type IResourceCardVideoPlayMode = 'hover' | 'auto'

/**
 * Suite surface defaults — change these to retarget Research vs Insights playback without hunting
 * through domain cards.
 */
export const RESOURCE_CARD_VIDEO_PLAY_MODE = {
    research: 'auto',
    insights: 'hover',
} as const satisfies Record<'research' | 'insights', IResourceCardVideoPlayMode>

/** Pure play decision: auto always plays; hover plays only while the media slot is hovered. */
export function resolveResourceCardShouldPlay(
    mode: IResourceCardVideoPlayMode,
    mediaHovered: boolean
): boolean {
    return mode === 'auto' || mediaHovered
}

type IResourceCardVideoContext = {
    videoPlayMode: IResourceCardVideoPlayMode
    /** True while the pointer is over the media slot (used for overlays too). */
    mediaHovered: boolean
    /** Derived play signal for the active mode. */
    shouldPlay: boolean
}

const ResourceCardVideoContext = createContext<IResourceCardVideoContext | null>(null)

/**
 * Live video play config from the enclosing ResourceCard. Throws outside a card so mis-wired
 * media fails loudly in development.
 */
export function useResourceCardVideo(): IResourceCardVideoContext {
    const ctx = useContext(ResourceCardVideoContext)
    if (!ctx) {
        throw new Error('useResourceCardVideo must be used within a ResourceCard')
    }
    return ctx
}

/** Same as {@link useResourceCardVideo}, but `null` when rendered outside a ResourceCard (e.g. table preview). */
export function useResourceCardVideoOptional(): IResourceCardVideoContext | null {
    return useContext(ResourceCardVideoContext)
}

export interface IResourceCardProps {
    /**
     * When set, the card is the open activator (pointer + keyboard) and gains hover shadow.
     * Domain hosts wire this to detail / preview — the shell does not know which.
     */
    onOpen?: () => void
    /** Accessible name when `onOpen` is set (e.g. `Open {name}`). */
    openLabel?: string
    /**
     * How videos in `media` should play. Prefer {@link RESOURCE_CARD_VIDEO_PLAY_MODE} at call sites
     * so Research vs Insights stay one config flip apart.
     */
    videoPlayMode?: IResourceCardVideoPlayMode
    className?: string
    /** Optional top chrome (brand row, score badge, …). */
    header?: ReactNode
    /** Media frame (poster, video, carousel). Hover for `videoPlayMode="hover"` is tracked here. */
    media?: ReactNode
    /** Title, KPIs, expandable rows, copy — the main content plane. */
    body?: ReactNode
    /**
     * Action row. When present, always rendered with a top rule so CTAs share one separator
     * across every surface that uses this shell.
     */
    footer?: ReactNode
}

/**
 * Resource-agnostic ad/creative card chrome: shared layout, hover shadow, open-on-click, footer
 * separator, and video play-mode context. Domain cards compose header/media/body/footer; they do
 * not re-own the shell or invent their own autoplay vs hover-play policy.
 */
export function ResourceCard({
    onOpen,
    openLabel,
    videoPlayMode = 'hover',
    className,
    header,
    media,
    body,
    footer,
}: IResourceCardProps) {
    const [mediaHovered, setMediaHovered] = useState(false)
    const shouldPlay = resolveResourceCardShouldPlay(videoPlayMode, mediaHovered)
    const videoContext = useMemo(
        () => ({ videoPlayMode, mediaHovered, shouldPlay }),
        [videoPlayMode, mediaHovered, shouldPlay]
    )

    const handleOpen = (event: MouseEvent | KeyboardEvent) => {
        if (!onOpen) return
        if (shouldIgnoreResourceCardOpen(event.target, event.currentTarget)) return
        // Text selection inside the card is a drag-select, not an open.
        if (typeof window !== 'undefined') {
            const selection = window.getSelection()
            if (selection && !selection.isCollapsed) return
        }
        onOpen()
    }

    return (
        <ResourceCardVideoContext.Provider value={videoContext}>
            <div
                {...(onOpen
                    ? {
                          role: 'button' as const,
                          tabIndex: 0,
                          'aria-label': openLabel,
                          onClick: handleOpen,
                          onKeyDown: (event: KeyboardEvent) => {
                              if (event.key !== 'Enter' && event.key !== ' ') return
                              if (shouldIgnoreResourceCardOpen(event.target, event.currentTarget)) return
                              event.preventDefault()
                              onOpen()
                          },
                      }
                    : {})}
                className={cn(
                    // A real <button> root would break percentage heights inside (anonymous button
                    // content box) — role=button keeps semantics without that layout trap.
                    'relative flex h-full flex-col overflow-hidden rounded-xl border border-stone-200 bg-white text-left',
                    onOpen &&
                        'cursor-pointer transition-shadow hover:shadow-md focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-orange-400 focus-visible:ring-offset-2',
                    className
                )}
            >
                {header}
                {media != null && (
                    // Real box (not `display:contents`) so mouseenter/leave fire; media slots are
                    // already full-width so this wrapper does not change card layout.
                    <div
                        className="w-full"
                        onMouseEnter={() => setMediaHovered(true)}
                        onMouseLeave={() => setMediaHovered(false)}
                    >
                        {media}
                    </div>
                )}
                {body}
                {footer != null && <div className="mt-auto border-t border-stone-200 px-3 py-2.5">{footer}</div>}
            </div>
        </ResourceCardVideoContext.Provider>
    )
}
